// Macalester College: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Macalester's Common Data Set 2025-2026
//    (CDS_2025-2026_Macalester-College_completed-1.pdf), linked from
//    Institutional Research's Institutional Data page: C1 applicants, admits
//    and enrollees for Fall 2025 (the residency table is blank); C8 test
//    policy for Fall 2027; C21/C22 early plans. Quirk: C21's early decision
//    counts (479 applied, 201 admitted) sit under the form's heading "For
//    the Fall 2024 entering class", but the 2024-2025 edition reports
//    different Fall 2024 counts (447 and 176), so they are read here as
//    Fall 2025, the class the rest of this edition covers. G0 says the
//    2026-2027 costs were not final when the CDS was filed, so the costs
//    come from the financial aid office instead.
//  - Macalester Admissions: Dates and Deadlines (dates for every round; the
//    weekdays match 2026-27, and the U.S. First-Year Applicants page heads
//    the same deadlines "Application Deadlines for a Fall 2027 start"),
//    U.S. First-Year Applicants (requirements, Early Decision Agreement,
//    test-optional policy, optional arts portfolio) and "When should I
//    apply?" (early decision and early action).
//  - Macalester Financial Aid, Tuition and Fees: the 2026-2027 Federal Cost
//    of Attendance, on campus. Travel ($100 to $1,000, by home state) is
//    a range and is left out; the waivable $3,329 health insurance is not
//    part of that cost of attendance.
//  - College Scorecard, school id 173902 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Macalester College Common Data Set 2025-2026',
        url: 'https://www.macalester.edu/institutional-research/institutionaldata/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Macalester Admissions, Dates and Deadlines',
        url: 'https://www.macalester.edu/admissions/deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Macalester Admissions, U.S. First-Year Applicants',
        url: 'https://www.macalester.edu/admissions/us-admissions/first-year/',
        read: '2026-10-01',
    },
    early: {
        label: 'Macalester Admissions, When should I apply?',
        url: 'https://www.macalester.edu/admissions/early-action-decision/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Macalester Financial Aid, Tuition and Fees',
        url: 'https://www.macalester.edu/financial-aid/tuition/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?173902-Macalester-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'macalester-college',
    kbId: 'macalester_college',
    name: 'Macalester College',
    shortName: 'Macalester',
    aliases: ['Mac'],
    location: 'Saint Paul, MN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Macalester visual identity colors); decoration only.
    branding: {
        primary: '#01426A',
        secondary: '#D44420',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/macalester_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8819,
        admitted: 2481,
        enrolled: 534,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Macalester admits to the college as a whole; its Common Data Set reports one first-year acceptance rate, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II together', applied: 479, admitted: 201 },
        ],
        note: 'Early decision counts as the 2025-2026 Common Data Set reports them, with both early decision rounds counted together; they are part of the totals above. The Common Data Set does not report early action counts, so there is no early action acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Macalester has no separate schools to apply to. One optional item depends on your interests:',
        rows: [
            { school: 'Art, music, theater or dance', requirement: 'Optional: you may submit an arts portfolio through your applicant portal before your application deadline. It is not required, and it has no bearing on your intended area of study.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 6', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 19', binding: false },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'January 31', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 14', binding: false },
        ],
        note: 'Financial aid priority dates are November 9 for Early Decision I and Early Action, January 8 for Early Decision II and January 22 for Regular Decision. Reply dates are December 21 for Early Decision I, February 8 for Early Decision II, and May 1 for Early Action and Regular Decision. Macalester takes the Common App or the QuestBridge Application; QuestBridge dates differ.',
        source: 'deadlines',
        binding: 'Early decision is for students who know Macalester is their first choice and is restrictive: if admitted, you must withdraw all other applications, assuming the financial aid award makes attendance at Macalester financially feasible. Early action is non-restrictive: you may apply to other colleges and reply by May 1.',
        bindingSource: 'early',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Macalester follows a test-optional admissions policy: you may send ACT, SAT, AP and IB scores or not. With or without scores, every applicant gets equal consideration for admission and scholarships. If you send scores, you may self-report them on the Common App, email them, or have official scores sent.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (includes required course materials)', amount: 74394 },
            { item: 'Activity fee', amount: 330 },
            { item: 'Food and housing (on campus)', amount: 18004 },
            { item: 'Textbooks and supplies', amount: 104 },
            { item: 'Computer purchase', amount: 954 },
            { item: 'Personal expenses', amount: 2400 },
        ],
        total: 96186,
        note: 'Macalester\'s 2026-2027 federal cost of attendance for a student living on campus. Its on-campus food and housing figure is an average across campus housing plus some food beyond the meal plan; the billed standard room and meal plan come to $17,430, for a comprehensive fee of $92,154. Travel, $100 to $1,000 depending on your home state, is not included.',
        source: 'costs',
        netPrice: {
            average: 27851,
            byIncome: [
                { income: '$0 to $30,000', amount: 15715 },
                { income: '$30,001 to $48,000', amount: 4569 },
                { income: '$48,001 to $75,000', amount: 15825 },
                { income: '$75,001 to $110,000', amount: 28539 },
                { income: 'Over $110,000', amount: 43770 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Macalester\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
